import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { selectAllClients } from '@/features/clients/clientsSelectors'
import { inviteUser, fetchInvites } from '@/features/users/usersSlice'
import { isValidEmail } from '@/features/clients/validate'
import { selectActiveOrgRole } from '@/features/auth/slices/sessionSlice'
import {
  ASSIGNABLE_ORG_ROLES,
  INVITE_LOCALES,
  toInviteLocale,
  type InvitableOrgRole,
  type InviteLocale
} from '@/features/users/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { cn } from '@/lib/utils'
import type { ApiError } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store'

/** The roles every admin may invite (ASSIGNABLE_ORG_ROLES); `owner` is added in
 * front for an owner caller only (INV-I10): the server refuses
 * `users.invite.owner-not-invitable` for anyone else, and this only hides what
 * it would refuse. */
const OWNER_INVITABLE_ROLES: readonly InvitableOrgRole[] = ['owner', ...ASSIGNABLE_ORG_ROLES]

const fieldClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground max-sm:min-h-[44px] max-sm:w-full'

/**
 * The invite panel (PX-W6.2, onboarding step 02's actor): email + org role +
 * optional client binding, validated client-side BEFORE submit; server errors
 * map inline (`user.email.invalid`, 409 duplicates, owner-not-invitable, …).
 */
export function InviteForm() {
  const { t, i18n } = useTranslation(['peopleAdmin', 'nav'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const clients = useAppSelector(selectAllClients)
  const callerIsOwner = useAppSelector(selectActiveOrgRole) === 'owner'
  const roleOptions = callerIsOwner ? OWNER_INVITABLE_ROLES : ASSIGNABLE_ORG_ROLES

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<InvitableOrgRole>('member')
  const [locale, setLocale] = useState<InviteLocale>(() => toInviteLocale(i18n.resolvedLanguage))
  const [clientId, setClientId] = useState('')
  const [validation, setValidation] = useState<Record<string, string>>({})
  const [serverError, setServerError] = useState<{ status: number; message: string } | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onEmailChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value)
    setValidation({})
    setServerError(null)
    setSentTo(null)
  }

  const onRoleChanged = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setRole(event.target.value as InvitableOrgRole)
  }

  const onLocaleChanged = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setLocale(event.target.value as InviteLocale)
  }

  const onClientChanged = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setClientId(event.target.value)
  }

  const onSubmitClicked = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    const errors: Record<string, string> = {}
    if (email.trim() === '') errors['email'] = 'peopleAdmin:invite.validation.emailRequired'
    else if (!isValidEmail(email)) errors['email'] = 'peopleAdmin:invite.validation.emailInvalid'
    setValidation(errors)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)
    setServerError(null)
    try {
      await dispatch(
        inviteUser({
          email: email.trim(),
          role,
          locale,
          // Only carry the binding when one is chosen — an empty string is
          // not a client id (the server would 400 `client.id.invalid`).
          ...(clientId !== '' ? { clientId } : {})
        })
      ).unwrap()
      setSentTo(email.trim())
      setEmail('')
      void dispatch(fetchInvites())
    } catch (rejected) {
      // The thunk's rejectValue IS the canonical ApiError.
      const message = toMessage(rejected as ApiError)
      if (message !== null) setServerError({ status: (rejected as ApiError).status, message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section
      aria-labelledby="invite-form-label"
      className="rounded-lg border border-border bg-card px-4 py-4"
    >
      <div id="invite-form-label">
        <SectionLabel>{t('invite.title')}</SectionLabel>
      </div>
      <form
        className="mt-3 flex flex-wrap items-end gap-3"
        noValidate
        onSubmit={(event) => void onSubmitClicked(event)}
      >

        <label className="flex flex-col gap-1 text-sm text-ink-soft max-sm:w-full">
          {t('invite.email')}
          <input
            type="email"
            value={email}
            onChange={onEmailChanged}
            aria-invalid={validation['email'] !== undefined}
            aria-describedby={validation['email'] !== undefined ? 'invite-email-error' : undefined}
            className={cn('w-64', fieldClass)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink-soft max-sm:w-full">
          {t('invite.role')}
          <select
            value={role}
            onChange={onRoleChanged}
            data-testid="invite-role"
            className={fieldClass}
          >
            {roleOptions.map((option) => (
              <option key={option} value={option}>
                {t(`peopleAdmin:roles.${option}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink-soft max-sm:w-full">
          {t('invite.client')}
          <select
            value={clientId}
            onChange={onClientChanged}
            className={cn('max-w-56 max-sm:max-w-none', fieldClass)}
          >
            <option value="">{t('invite.clientNone')}</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink-soft max-sm:w-full">
          {t('invite.locale')}
          <select value={locale} onChange={onLocaleChanged} data-testid="invite-locale" className={fieldClass}>
            {INVITE_LOCALES.map((option) => (
              <option key={option} value={option}>
                {t(`nav:language.${option}`)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-primary max-sm:min-h-[44px] max-sm:w-full px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {submitting ? t('invite.sending') : t('invite.submit')}
        </button>
      </form>
      <p className="mt-2 text-xs text-muted-foreground">
        {callerIsOwner ? t('invite.roleHintOwner') : t('invite.roleHint')}
      </p>

      {validation['email'] !== undefined && (
        <p id="invite-email-error" role="alert" className="mt-2 text-sm text-blocked">
          {t(validation['email'] ?? '')}
        </p>
      )}
      {serverError !== null && (
        <p role="alert" className="mt-2 text-sm text-blocked">
          {serverError.message}
        </p>
      )}
      {sentTo !== null && (
        <p role="status" className="mt-2 text-sm text-done">
          {t('invite.sent', { email: sentTo })}
        </p>
      )}
    </section>
  )
}
